<script lang="ts" setup>
import { useStorage } from '@vueuse/core'
import { ArrowLeftEndOnRectangleIcon, Cog6ToothIcon, LockClosedIcon, XMarkIcon } from '@heroicons/vue/24/outline'

import { useUiStore } from '@/stores'
import { usePlatformNavigation } from '@/composables'

const emits = defineEmits<{
  (event: 'navItemClicked'): void
}>()
const supabaseClient = useSupabaseClient()
const supabaseUser = useSupabaseUser()
const uiStore = useUiStore()
const { navigation } = usePlatformNavigation()

const { sidebarShown } = storeToRefs(uiStore)
const { hideSidebar } = uiStore

function handleNavItemClicked() {
  emits('navItemClicked')
}

async function signOut() {
  await supabaseClient.auth.signOut()
}

const hideNavItems = ref<boolean>(false)
const isUserAdmin = computed(() => supabaseUser.value?.app_metadata?.role === 'admin')

watchEffect(() => {
  hideNavItems.value = useStorage('refloatDisableRedirection', 'false', sessionStorage).value === 'true'
}, { flush: 'post' })
</script>

<template>
  <HeadlessTransitionRoot as="template" :show="sidebarShown">
    <HeadlessDialog as="div" class="relative z-50 lg:hidden" @close="hideSidebar()">
      <HeadlessTransitionChild as="template" enter="transition-opacity ease-linear duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="transition-opacity ease-linear duration-300" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-gray-900/50" />
      </HeadlessTransitionChild>

      <div class="fixed inset-0 flex">
        <HeadlessTransitionChild as="template" enter="transition ease-in-out duration-300 transform" enter-from="-translate-x-full" enter-to="translate-x-0" leave="transition ease-in-out duration-300 transform" leave-from="translate-x-0" leave-to="-translate-x-full">
          <HeadlessDialogPanel class="relative mr-16 max-w-xs w-full flex flex-1">
            <HeadlessTransitionChild as="template" enter="ease-in-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in-out duration-300" leave-from="opacity-100" leave-to="opacity-0">
              <div class="absolute left-full top-0 w-16 flex justify-center pt-5">
                <button type="button" class="border-none p-2.5 outline-none ring-none -m-2.5 focus:border-none focus:outline-none focus:ring-none" @click="hideSidebar()">
                  <span class="sr-only">Close sidebar</span>
                  <XMarkIcon class="h-6 w-6 text-white" aria-hidden="true" />
                </button>
              </div>
            </HeadlessTransitionChild>

            <div class="flex grow flex-col gap-y-8 overflow-y-auto bg-rfblue-800 px-6 py-8">
              <div class="h-16 flex shrink-0 flex-col gap-y-4">
                <img class="h-8 w-auto" src="~/assets/refloat-logo-with-text.svg" alt="Refloat company logo">
              </div>
              <nav class="flex flex-1 flex-col">
                <ul role="list" class="flex flex-1 flex-col gap-y-7" @click="handleNavItemClicked()">
                  <li v-if="!hideNavItems">
                    <ul role="list" class="-mx-2 space-y-1">
                      <li v-for="item in navigation" :key="item.name">
                        <NuxtLink :to="item.link" class="group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6" :class="[item.current ? 'bg-rfblue-600 text-white' : 'text-gray-100 hover:text-white hover:bg-rfblue-600']">
                          <component :is="item.icon" class="h-6 w-6 shrink-0" :class="[item.current ? 'text-white' : 'text-gray-100 group-hover:text-white']" aria-hidden="true" />
                          {{ item.name }}
                        </NuxtLink>
                      </li>
                    </ul>
                  </li>
                  <li class="mt-auto">
                    <ul role="list" class="-mx-2 space-y-1">
                      <li v-if="isUserAdmin && !hideNavItems">
                        <NuxtLink class="group w-full flex gap-x-3 rounded-md p-2 text-sm text-gray-100 font-semibold leading-6 hover:bg-rfblue-600 hover:text-white" to="/admin/sync-stripe-data">
                          <LockClosedIcon class="h-6 w-6 shrink-0 text-gray-100 group-hover:text-white" aria-hidden="true" />
                          Admin
                        </NuxtLink>
                      </li>
                      <li v-if="!hideNavItems">
                        <NuxtLink class="group w-full flex gap-x-3 rounded-md p-2 text-sm text-gray-100 font-semibold leading-6 hover:bg-rfblue-600 hover:text-white" to="/account-settings">
                          <Cog6ToothIcon class="h-6 w-6 shrink-0 text-gray-100 group-hover:text-white" aria-hidden="true" />
                          Account Settings
                        </NuxtLink>
                      </li>
                      <li>
                        <button class="group w-full flex gap-x-3 rounded-md p-2 text-sm text-gray-100 font-semibold leading-6 hover:bg-rfblue-600 hover:text-white" @click="signOut()">
                          <ArrowLeftEndOnRectangleIcon class="h-6 w-6 shrink-0 text-gray-100 group-hover:text-white" aria-hidden="true" />
                          Logout
                        </button>
                      </li>
                    </ul>
                  </li>
                </ul>
              </nav>
            </div>
          </HeadlessDialogPanel>
        </HeadlessTransitionChild>
      </div>
    </HeadlessDialog>
  </HeadlessTransitionRoot>
</template>
